// Comportement d'une box organigramme posée dans un modèle. Le modèle ne peut
// pas montrer d'organigramme — il n'existe qu'une fois le document injecté dans
// une proposition —, il décrit donc ce qui devra s'y passer.
export const ORG_CHART_MODES = {
  // Le commercial choisit lui-même dans la liste de son agence et de ses parentes.
  CHOOSE: 'choose',
  // Un organigramme vierge est créé à l'injection.
  BLANK: 'blank',
  // Résolu à l'injection : organigramme de l'agence de la proposition...
  ENTITY: 'entity',
  // ... ou de son agence parente (le siège si elle n'en a pas, comme la
  // variable texte « agence parente »).
  PARENT_ENTITY: 'parent_entity',
  // ... ou d'une agence choisie explicitement dans le modèle (n'importe laquelle,
  // pas seulement celle de la proposition ou sa parente). Pour les trois modes
  // d'agence, un tag optionnel désigne l'organigramme, avec repli sur celui par
  // défaut de l'agence (cf. resolver serveur).
  AGENCY_TAG: 'agency_tag',
  // Organigramme composé dans le modèle lui-même.
  TEMPLATE: 'template',
};

export const orgChartMode = (box) => box?.orgChartMode ?? ORG_CHART_MODES.CHOOSE;

// « Organigramme d'une agence » : un seul choix dans le panneau, l'agence visée
// (concernée, parente, choisie) se règle ensuite. Tous résolus par le serveur au
// moment de l'injection.
export const ORG_CHART_AGENCY_MODES = [
  ORG_CHART_MODES.ENTITY,
  ORG_CHART_MODES.PARENT_ENTITY,
  ORG_CHART_MODES.AGENCY_TAG,
];

export const isAgencyOrgChartMode = (mode) => ORG_CHART_AGENCY_MODES.includes(mode);

export const isVariableOrgChart = (box) => isAgencyOrgChartMode(orgChartMode(box));

// Paramètres de résolution envoyés au serveur. L'agence explicite ne vaut qu'en
// mode « agence choisie » : une box repassée ensuite en « agence concernée » la
// garde en mémoire, et le serveur la ferait primer sur le scope. Le tag, lui,
// vaut pour les trois modes d'agence.
export const orgChartResolveTarget = (box) => ({
  entityId:
    orgChartMode(box) === ORG_CHART_MODES.AGENCY_TAG
      ? box?.orgChartTargetEntityId ?? undefined
      : undefined,
  tagId: box?.orgChartTargetTagId ?? undefined,
});

// La résolution a lieu une fois par injection, et c'est le drapeau posé à ce
// moment-là qui le dit — pas la présence d'un organigramme sur la box. Une box
// peut en effet en traîner un qui vient du modèle (mode « organigramme de ce
// modèle » abandonné en cours de route) : s'y fier revenait à afficher cet
// organigramme-là et à ne jamais interroger l'agence.
// Le choix manuel du commercial, lui, repasse la box en mode « choisi dans la
// proposition » : il n'est donc jamais réécrit.
export const shouldResolveOrgChart = ({ box, isTemplate, proposalId }) =>
  !isTemplate && !!proposalId && isVariableOrgChart(box) && !box?.orgChartResolved;

// Même drapeau que la résolution, et pour la même raison : une box en mode
// vierge peut traîner l'organigramme d'un autre mode.
export const shouldCreateBlankOrgChart = ({ box, isTemplate, proposalId }) =>
  !isTemplate &&
  !!proposalId &&
  orgChartMode(box) === ORG_CHART_MODES.BLANK &&
  !box?.orgChartResolved;

// Changement de mode depuis le modèle. Seul « organigramme de ce modèle »
// laisse la box porter un organigramme : les autres modes le choisissent ou le
// résolvent dans la proposition, et une box qui en traîne encore un ne
// déclenche jamais la résolution (cf. shouldResolveOrgChart) — elle affiche
// l'organigramme du modèle à sa place.
// Celui du modèle est mis de côté plutôt que jeté : revenir sur ce mode le
// retrouve, sans le recomposer ni l'abandonner en base.
export const orgChartModeChange = (box, mode) => {
  const emptyContent = { imageUrl: null, name: null, ratio: null };
  // Copie du contenu, pas la référence : la box est mutée dans un brouillon
  // immer, et garder deux fois le même objet ferait bouger la mise de côté en
  // même temps que la box.
  const kept = box?.orgChartId
    ? { id: box.orgChartId, content: { ...box.content } }
    : box?.orgChartTemplateChart ?? null;

  if (mode === ORG_CHART_MODES.TEMPLATE) {
    return {
      orgChartMode: mode,
      orgChartId: kept?.id ?? null,
      orgChartSourceId: kept?.id ?? null,
      content: kept?.content ?? emptyContent,
      orgChartTemplateChart: kept,
    };
  }

  return {
    orgChartMode: mode,
    orgChartId: null,
    orgChartSourceId: null,
    content: emptyContent,
    orgChartTemplateChart: kept,
  };
};

// L'organigramme du modèle est figé dans la proposition, sauf déverrouillage
// explicite depuis le modèle : sans ça chaque proposition en ferait sa version.
export const isOrgChartEditableInProposal = (box) =>
  orgChartMode(box) !== ORG_CHART_MODES.TEMPLATE || box?.orgChartUnlocked === true;
